import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { apiCall } from '../services/api';
import { BatchRecord } from '../hooks/useBatches';
import FileTree, { isExpired, formatCountdown } from './FileTree';

interface BatchItemProps {
  batch: BatchRecord;
  bucketName: string;
  canDownload: boolean;
  onDelete: (jobId: string) => Promise<void>;
}

const BatchItem: React.FC<BatchItemProps> = ({ batch, bucketName, canDownload, onDelete }) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  // Local override for expiry — set when 410 confirms object is gone
  const [localExpired, setLocalExpired] = useState<boolean>(false);

  // Compute expiry from urlExpiresAt at render time (same call drives both label and disabled state — Req 4.1, 4.5)
  const expired = localExpired || isExpired(batch.urlExpiresAt);
  const countdown = expired ? 'Expired' : formatCountdown(batch.urlExpiresAt);

  const handleDownload = async (): Promise<void> => {
    // Req 3.6 — ignore additional clicks while in-flight
    if (isDownloading) return;
    setIsDownloading(true); // Req 3.5 — within 200ms (synchronous)
    setError(null);
    try {
      const response = await apiCall('/s3/regenerate-zip-url/' + bucketName, {
        method: 'POST',
        body: JSON.stringify({ jobId: batch.jobId }),
      });
      // Req 3.2 — initiate download then update expiry
      const link = document.createElement('a');
      link.href = response.downloadUrl;
      link.download = batch.zipFileName || 'download.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Update displayed expiry only after download successfully initiated
      // (handled by parent re-render; local state updated via batch prop if needed)
    } catch (err: unknown) {
      // Req 3.3 — 410 Gone: mark as expired
      const isGone = err instanceof Error && (
        err.message.includes('410') || err.message.toLowerCase().includes('gone') ||
        err.message.toLowerCase().includes('expired')
      );
      if (isGone) {
        setLocalExpired(true); // Req 4.4
      } else {
        // Req 3.4 — other errors: show inline error, restore state
        setError(err instanceof Error ? err.message : 'Failed to generate download link');
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    setIsDeleting(true);
    try {
      await onDelete(batch.jobId);
      // Parent removes the item optimistically — no local state needed
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete archive');
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatSize = (bytes: number | null): string => {
    if (bytes === null || bytes === 0) return '—';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const fileName = batch.zipFileName
    ? batch.zipFileName.split('/').pop() || batch.zipFileName
    : 'archive.zip';

  return (
    <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
      {/* Row header — click to expand/collapse */}
      <div className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors">
        <button
          type="button"
          className="flex items-center gap-3 flex-1 min-w-0 text-left"
          onClick={() => setIsExpanded(prev => !prev)}
          aria-expanded={isExpanded}
        >
          {/* Expand chevron */}
          <svg
            className={`w-4 h-4 shrink-0 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          {/* Archive icon */}
          <svg className="w-5 h-5 shrink-0 text-yellow-500" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
            <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" />
            <path fillRule="evenodd" d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" clipRule="evenodd" />
          </svg>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{fileName}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {batch.archivedFileCount !== null ? `${batch.archivedFileCount} file${batch.archivedFileCount !== 1 ? 's' : ''}` : '—'}
              {' · '}
              {formatSize(batch.totalSize)}
            </p>
          </div>
        </button>

        {/* Expiry badge + actions */}
        <div className="flex items-center gap-2 ml-3 shrink-0">
          {/* Expiry label — same `expired` variable drives both label and button disabled (Req 4.1, 4.5) */}
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
            expired
              ? 'bg-red-100 text-red-700'
              : 'bg-green-100 text-green-700'
          }`}>
            {countdown}
          </span>

          {/* Download button */}
          {canDownload && (
            <button
              type="button"
              disabled={expired || isDownloading}
              onClick={handleDownload}
              aria-label="Download archive"
              className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isDownloading ? (
                <span className="flex items-center gap-1">
                  <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  Downloading…
                </span>
              ) : 'Download'}
            </button>
          )}

          {/* Delete button */}
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(true)}
            aria-label="Delete archive"
            className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
          >
            Delete
          </button>
        </div>
      </div>

      {/* Inline error */}
      {error && (
        <div className="px-4 py-2 bg-red-50 border-t border-red-100">
          <p className="text-xs text-red-600">{error}</p>
        </div>
      )}

      {/* File tree — keyed by jobId so it fully remounts on collapse/re-expand (Req 2.5) */}
      {isExpanded && (
        <div className="px-4 py-3 border-t border-gray-100 bg-gray-50">
          <p className="text-xs text-gray-500 mb-2">
            {batch.archivedFileCount !== null ? `${batch.archivedFileCount} file${batch.archivedFileCount !== 1 ? 's' : ''} archived` : 'Files archived'}
          </p>
          <FileTree key={batch.jobId} files={batch.files} />
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div
            className="bg-white rounded-xl p-6 max-w-sm mx-4 shadow-xl"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold text-gray-900 mb-2">Delete Archive?</h3>
            <p className="text-sm text-gray-600 mb-4">
              Delete <span className="font-medium text-gray-900">{fileName}</span>? This cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                className="px-3 py-1.5 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {isDeleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
};

export default BatchItem;
